import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Search, X } from "lucide-react";

const selCls = "h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500";

// Live search (filters while typing) + dropdown filters built from the rows themselves.
export const useSmartFilter = (rows, { text, filters, placeholder, testid, hidden, initialSort = null }) => {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const opts = useMemo(() => Object.fromEntries(filters.map((f) => [f.key,
    [...new Set(rows.map(f.get).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)))])), [rows, filters]);
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const filtered = rows.filter((r) => {
    if (hidden && hidden(r, sel)) return false;
    if (filters.some((f) => sel[f.key] && f.get(r) !== sel[f.key])) return false;
    const hay = text.map((k) => (typeof k === "function" ? k(r) : r[k]) || "").join(" ").toLowerCase();
    return words.every((w) => hay.includes(w));
  });
  const active = q || Object.values(sel).some(Boolean);
  const [sort, setSort] = useState({ key: initialSort, dir: 1 });
  if (sort.key) {
    const val = (r) => { const v = typeof sort.key === "function" ? sort.key(r) : r[sort.key]; return v == null || v === "" || v === "—" ? null : v; };
    filtered.sort((x, y) => { const a = val(x), b = val(y);
      if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
      return String(a).localeCompare(String(b), "en", { numeric: true, sensitivity: "base" }) * sort.dir; });
  }
  const Th = ({ k, children, className = "pr-3" }) => (
    <th className={className}>
      <button type="button" onClick={() => setSort((s) => (s.key === k ? { key: k, dir: -s.dir } : { key: k, dir: 1 }))}
        className="inline-flex items-center gap-1 uppercase hover:text-slate-800" data-testid={`${testid}-sort-${typeof k === "string" ? k : children}`}>
        {children}{sort.key === k ? (sort.dir === 1 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </th>);
  const ui = (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testid}-filters`}>
      <div className="relative w-full sm:w-72">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder}
          className="h-9 w-full rounded-md border border-gray-300 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" data-testid={`${testid}-search`} />
      </div>
      {filters.map((f) => (
        <select key={f.key} className={selCls} value={sel[f.key] || ""} onChange={(e) => setSel((s) => ({ ...s, [f.key]: e.target.value }))} data-testid={`${testid}-filter-${f.key}`}>
          <option value="">{f.label}: All</option>
          {opts[f.key].map((o) => <option key={o} value={o}>{o}</option>)}
        </select>))}
      {active && <button type="button" onClick={() => { setQ(""); setSel({}); }} className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-xs text-gray-600 hover:bg-gray-100" data-testid={`${testid}-clear`}>
        <X className="h-3.5 w-3.5" />Clear</button>}
    </div>
  );
  return { ui, filtered, total: rows.length, Th };
};
